import { Color3, type Mesh, MeshBuilder, PBRMaterial, type Scene, TransformNode } from "@babylonjs/core";
import { BASE_LIFT } from "./ruin";

export const STONE_RADIUS = 0.85;
export const STONE_THICKNESS = 0.3;
/** Lift above the flattened terrain so the top face is never coplanar with it (z-fighting). */
const LAYERS = 3;

function micaMaterial(scene: Scene): PBRMaterial {
  const m = new PBRMaterial("mica", scene);
  m.albedoColor = Color3.FromHexString("#d8c7a0");
  m.metallic = 0.05; // no environment texture in the scene: a metallic surface would reflect nothing and render dark
  m.roughness = 0.25;
  m.clearCoat.isEnabled = true;
  m.clearCoat.intensity = 0.6;
  m.iridescence.isEnabled = true;
  m.iridescence.intensity = 0.5;
  m.iridescence.minimumThickness = 300;
  m.iridescence.maximumThickness = 600;
  return m;
}

/**
 * Flat pentagonal mica stone at the world origin (spec 2d §2.1): three 0.1 m layers, the upper two turned
 * 4° and nudged a few centimetres, one tip pointing north to the library door. It rests on the ruin base
 * in the carved plaza (groundY is the plaza floor), so all three layers show; climbing on is a jump.
 */
export function buildNomenStone(scene: Scene, pos: [number, number], groundY: number): { meshes: Mesh[]; glow: Mesh[] } {
  const root = new TransformNode("nomen-stone", scene);
  root.position.set(pos[0], groundY + BASE_LIFT, pos[1]);
  const m = micaMaterial(scene);
  // upper two layers: same mica with a subtle warm emissive so the thin rim blooms in the GlowLayer
  const rim = m.clone("mica-rim");
  rim.emissiveColor = Color3.FromHexString("#d8c7a0").scale(0.35);
  const h = STONE_THICKNESS / LAYERS;
  const meshes: Mesh[] = [];
  for (let i = 0; i < LAYERS; i++) {
    // CreateCylinder with tessellation 5 puts a vertex on local +x; rotate −90° about y so it points to +z
    const layer = MeshBuilder.CreateCylinder(`nomen-stone:layer${i}`, { diameter: STONE_RADIUS * 2 * (1 - i * 0.04), height: h, tessellation: 5 }, scene);
    layer.material = i === 0 ? m : rim;
    layer.parent = root;
    layer.rotation.y = -Math.PI / 2 + (i === 0 ? 0 : ((i === 1 ? 4 : -4) * Math.PI) / 180);
    layer.position.set(i === 0 ? 0 : (i === 1 ? 0.02 : -0.03), h / 2 + i * h, i === 0 ? 0 : (i === 1 ? -0.025 : 0.02));
    layer.checkCollisions = true;
    layer.isPickable = false;
    meshes.push(layer);
  }
  return { meshes, glow: meshes.slice(1) };
}
